// NodeViewer.js
import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tooltip,
  Alert,
  AlertTitle,
} from "@mui/material";
import { buildNumberedList } from "./nodeUtils";

const safeText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const SimpleNodeViewer = ({
  root,
  selectedCategory = null,
  nodeConfigs = {},
  onSelectNode,
  selectedNodeId,
  showTooltip = true,
}) => {
  let lines = [];

  try {
    lines = buildNumberedList(root, nodeConfigs);
  } catch (error) {
    console.error("NodeViewer: buildNumberedList failed", error);
    console.error("Root:", root);

    return (
      <Alert severity="error">
        <AlertTitle>Unable to display ruleset</AlertTitle>

        <Typography variant="body2">
          The ruleset contains data that could not be rendered.
        </Typography>

        <Box
          component="pre"
          sx={{
            mt: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "0.75rem",
          }}
        >
          {safeText(error?.message || error)}
        </Box>
      </Alert>
    );
  }

  if (!Array.isArray(lines)) {
    console.error("NodeViewer: buildNumberedList returned:", lines);

    return (
      <Alert severity="error">
        <AlertTitle>Invalid ruleset</AlertTitle>
        <Typography variant="body2">
          The ruleset could not be converted into displayable rules.
        </Typography>
      </Alert>
    );
  }

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 1.5,
        background: "#f9f9ff",
        border: "1px solid #e0e0e0",
      }}
      elevation={1}
    >
      <Box sx={{ mb: 2, p: 1 }}>
        {lines.map((line, index) => {
          if (!line) {
            return null;
          }

          if (line.isOutcomeHeader) {
            return (
              <Box
                key={`viewer-header-${line.id ?? index}`}
                sx={{
                  ml: (line.depth || 0) * 2,
                  my: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6c63ff",
                    fontWeight: 600,
                  }}
                >
                  {safeText(line.text)}
                </Typography>
              </Box>
            );
          }

          const isSelected = selectedNodeId === line.id;

          // const nodeName =
          //   line.node?.node === "Category Check"
          //     ? `Category - ${safeText(selectedCategory)}`
          //     : safeText(line.node?.node);

          const lineText = safeText(line.text);

          return (
            <Box
              key={`viewer-${line.id ?? index}`}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                ml: Math.max(0, line.depth || 0) * 2,
                py: 0.5,
              }}
            >
              {showTooltip ? (
                <Tooltip title="Click to focus this rule in the editor" arrow>
                  <Button
                    size="small"
                    onClick={() =>
                      onSelectNode &&
                      onSelectNode(
                        isSelected
                          ? { id: null, numbering: null }
                          : { id: line.id, numbering: line.numbering },
                      )
                    }
                    variant={isSelected ? "contained" : "outlined"}
                    sx={{
                      minWidth: 32,
                      width: "fit-content",
                      height: 28,
                      fontSize: "0.8rem",
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      ...(line.node?.fields?.deactivated && {
                        color: "#9e9e9e",
                        borderColor: "#bdbdbd",
                        backgroundColor: isSelected ? "#e0e0e0" : "transparent",
                        textDecoration: "line-through",
                      }),
                    }}
                  >
                    Rule - {safeText(line.numbering) || "•"}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  onClick={() =>
                    onSelectNode &&
                    onSelectNode(
                      isSelected
                        ? { id: null, numbering: null }
                        : { id: line.id, numbering: line.numbering },
                    )
                  }
                  variant={isSelected ? "contained" : "outlined"}
                  sx={{
                    minWidth: 32,
                    width: "fit-content",
                    height: 28,
                    fontSize: "0.8rem",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    ...(line.node?.fields?.deactivated && {
                      color: "#9e9e9e",
                      borderColor: "#bdbdbd",
                      backgroundColor: isSelected ? "#e0e0e0" : "transparent",
                      textDecoration: "line-through",
                    }),
                  }}
                >
                  Rule - {safeText(line.numbering) || "•"}
                </Button>
              )}

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    color: line.node?.fields?.deactivated
                      ? "#9e9e9e"
                      : isSelected
                        ? "#1e88e5"
                        : "inherit",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {lineText}
                  {line.node?.fields?.deactivated && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: "#9e9e9e", fontStyle: "italic" }}
                    >
                      (Inactive)
                    </Typography>
                  )}
                </Typography>

                {line.node?.error && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    <AlertTitle
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      Error
                    </AlertTitle>

                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {safeText(line.node.error)}
                    </Typography>
                  </Alert>
                )}

                {line.node?.traceback?.traceback && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "#fff3e0",
                      border: "1px solid #ff9800",
                      borderRadius: 1,
                      maxWidth: "100%",
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#e65100",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Traceback:
                    </Typography>

                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#d84315",
                        margin: 0,
                      }}
                    >
                      {safeText(line.node.traceback.traceback)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default SimpleNodeViewer;
